<template>
  <el-card
    style="height: 100%; width: 100%"
    body-class="flex flex-col gap-4"
    :body-style="{
      height: '100%',
      width: '100%',
    }"
    shadow="always">
    <ApplicationDetailsLicencesTable
      v-if="application && currentSubAccount"
      ref="licenseTable"
      v-model:sub-account="currentSubAccount"
      data-tutorial-id="application-details-licences-card"
      :application="application"
      :application-licenses="allSoftwareLicenses"
      @licenses-changed="handleLicensesChanged"
      @export-licenses="handleExportLicenses"
      @import-licenses="handleImportLicenses"
      @open-add-user="handleOpenAddUser" />
  </el-card>

  <ExportDialog
    v-model:visibility="visibilityExportDialog"
    :sub-account-id="currentSubAccount?.id ?? undefined"
    :account-filter="licenseTable?.accountFilter"
    :application-id="application?._id" />

  <SmDialog
    v-model:visibility="showLicenseImportDialog"
    :title="i18n.t('licenseCsvImport.csvImportHeading')">
    <AccountCsvImport
      v-if="application"
      :application-id="application._id"
      @successful-import="handleSuccessfulImport" />
  </SmDialog>

  <AccountAddDialog
    v-if="application"
    v-model:visibility="visibilityAccountAddDialog"
    :software-licenses="allSoftwareLicenses"
    :application="application"
    :sub-account="currentSubAccount ?? undefined"
    @user-added="handleLicensesChanged" />
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { SoftwareLicense, SubAccount_Output } from '@/client'
  import { AccountFilters } from '@/stores/account/account.filter'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
  import { useRoute } from 'vue-router'

  const i18n = useI18n()
  const route = useRoute()
  const applicationDetailsStore = useApplicationDetailsStore()

  const applicationIdParam = computed(() => {
    const param = route.params.id
    return Array.isArray(param) ? param[0] : (param as string)
  })

  watch(
    applicationIdParam,
    (id) => {
      if (id) {
        applicationDetailsStore.loadApplicationDetails(id)
      }
    },
    { immediate: true }
  )

  const application = computed(() => applicationDetailsStore.application)
  const allSoftwareLicenses = computed<SoftwareLicense[]>(
    () => applicationDetailsStore.licences
  )

  const licenseTable = ref<{ accountFilter?: AccountFilters }>()

  const currentSubAccount = ref<SubAccount_Output | null>(null)

  watch(
    [() => route.params.subAccountId, application],
    ([newSubAccountId, app]) => {
      if (!app) return

      const id = Array.isArray(newSubAccountId)
        ? newSubAccountId[0]
        : ((newSubAccountId as string) ?? null)

      if (id && app.sub_accounts) {
        currentSubAccount.value = app.sub_accounts[id] ?? null
        return
      }

      if (app.sub_accounts) {
        const firstKey = Object.keys(app.sub_accounts)[0]
        currentSubAccount.value = app.sub_accounts[firstKey] ?? null
      } else {
        currentSubAccount.value = null
      }
    },
    { immediate: true }
  )

  async function handleLicensesChanged() {
    await applicationDetailsStore.refreshLicences()
  }

  const visibilityExportDialog = ref(false)
  const showLicenseImportDialog = ref(false)
  const visibilityAccountAddDialog = ref(false)

  function handleExportLicenses() {
    visibilityExportDialog.value = true
  }

  function handleImportLicenses() {
    showLicenseImportDialog.value = true
  }

  function handleOpenAddUser() {
    visibilityAccountAddDialog.value = true
  }

  function handleSuccessfulImport() {
    showLicenseImportDialog.value = false
    handleLicensesChanged()
  }
</script>
